/**
 * Utility to prevent excessive refresh calls that can cause page reload loops
 */

class RefreshDebouncer {
  private lastCalls: Map<string, number> = new Map()
  private defaultDelay = 5000 // 5 seconds default delay

  /**
   * Check if a refresh call should be allowed based on the key and delay
   * @param key - Unique identifier for the refresh operation
   * @param delay - Minimum delay between calls in milliseconds (default: 5000)
   * @returns true if call is allowed, false if debounced
   */
  shouldAllowRefresh(key: string, delay: number = this.defaultDelay): boolean {
    const now = Date.now()
    const lastCall = this.lastCalls.get(key) || 0
    
    if (now - lastCall >= delay) {
      this.lastCalls.set(key, now)
      return true
    }
    
    console.log(`Debouncing refresh call for key: ${key}. Last call was ${now - lastCall}ms ago`)
    return false
  }

  /**
   * Force allow the next refresh call for a specific key
   * @param key - Unique identifier for the refresh operation
   */
  forceAllowNext(key: string): void {
    this.lastCalls.delete(key)
  }

  /**
   * Clear all stored call times (useful for testing or manual refresh)
   */
  clear(): void {
    this.lastCalls.clear()
  }

  /**
   * Clear a specific key's call time
   */
  clearKey(key: string): void {
    this.lastCalls.delete(key)
  }

  /**
   * Get the time since the last call for a specific key
   */
  getTimeSinceLastCall(key: string): number {
    const lastCall = this.lastCalls.get(key) || 0
    return Date.now() - lastCall
  }
}

// Export a singleton instance
export const refreshDebouncer = new RefreshDebouncer()

export default refreshDebouncer
